import React, { useState, useCallback } from 'react';
import { User, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import SearchInput from '@components/common/SearchInput';
import { formatPersonName } from '@utils/stringUtils';
import Button from '@components/common/Button';
import styles from './Header.module.css';

interface HeaderProps {
  title?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (searchTerm: string) => void;
  actions?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({
  title,
  showSearch = false,
  searchPlaceholder = 'Search',
  onSearch,
  actions
}) => {
  const { user, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (onSearch) {
        onSearch(value);
      }
    },
    [onSearch]
  );

  const handleLogout = useCallback(() => {
    logout();
    setShowUserMenu(false);
  }, [logout]);

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        {title && (
          <div className={styles.headerTitle}>
            <h1 className={styles.pageTitle}>{title}</h1>
          </div>
        )}
      </div>

      <div className={styles.headerRight}>
        {showSearch && (
          <div className={styles.headerSearch}>
            <SearchInput value={searchTerm} onChange={handleSearchChange} placeholder={searchPlaceholder} />
          </div>
        )}

        {actions && <div className={styles.headerActions}>{actions}</div>}

        {user && (
          <div className={styles.headerUser}>
            <button className={styles.userMenuTrigger} onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className={styles.userAvatar}>
                <User size={20} />
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>
                  {formatPersonName(user)}
                </span>
                <span className={styles.userRole}>{user.roles?.join(', ') || 'Contact'}</span>
              </div>
              <ChevronDown size={16} />
            </button>

            {showUserMenu && (
              <div className={styles.userMenu}>
                <div className={styles.userMenuHeader}>
                  <div className={styles.userDetails}>
                    <span className={styles.userFullName}>
                      {formatPersonName(user)}
                    </span>
                    <span className={styles.userEmail}>{user.email}</span>
                    <span className={styles.userRoleBadge}>{user.roles?.join(', ') || 'Contact'}</span>
                  </div>
                </div>
                <div className={styles.userMenuSeparator}></div>
                <div className={styles.userMenuActions}>
                  <Button variant="ghost" size="sm" onClick={handleLogout} className={styles.logoutButton}>
                    <LogOut size={16} />
                    Logout
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close user menu */}
      {showUserMenu && <button type="button" className={styles.overlay} onClick={() => setShowUserMenu(false)} aria-label="Close menu" />}
    </header>
  );
};

export default Header;
